import React from 'react';
import { Navigate } from 'react-router-dom';
import useAdminAuth from '../../hooks/useAdminAuth';
import { LoadingFallback } from '../common/LoadingIndicators';
import { useAuth } from '@clerk/clerk-react';

interface AdminGateProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

/**
 * AdminGate component that only renders its children if the user is an admin.
 * Otherwise, it renders the fallback or redirects.
 */
const AdminGate: React.FC<AdminGateProps> = ({ 
  children, 
  fallback, 
  redirectTo = '/access-denied'
}) => {
  const { isSignedIn } = useAuth();
  const { isAdmin, loading } = useAdminAuth();

  // First check if user is signed in
  if (!isSignedIn) {
    return <Navigate to="/login-required" replace />;
  }

  // Show loading spinner while checking admin status
  if (loading) {
    return <LoadingFallback />;
  }

  // If user is not an admin
  if (!isAdmin) {
    // If fallback is provided, render it
    if (fallback) {
      return <>{fallback}</>;
    }
    
    // Otherwise redirect to the specified path
    return <Navigate to={redirectTo} replace />;
  }

  // User is an admin, render the children
  return <>{children}</>;
};

export default AdminGate;
